import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { login, logout } from "api/authApi"
import { useNavigate } from "react-router-dom"
import { validateToken as validateTokenApi } from "api/authApi"
import { getToken, removeToken, setToken } from "utils/helpersUtils"

export const AUTH_QUERY_KEY = "auth"

export const useLogin = () => {
  const queryClient = useQueryClient()
  const { login: authLogin } = useAuth()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: login,
    onSuccess: data => {
      const token = data?.data?.access_token
      const user = data?.data?.user

      // Store token if available
      if (token) {
        authLogin(token, user)
        navigate("/", { replace: true })
      }

      // Invalidate any existing auth queries to refresh user data
      queryClient.invalidateQueries([AUTH_QUERY_KEY])
    },
  })
}

// Helper function for logout cleanup
const handleLogoutCleanup = (queryClient, navigate) => {
  removeToken()
  queryClient.removeQueries([AUTH_QUERY_KEY])
  queryClient.clear() // Optional: clear all queries if needed
  navigate("/login", { replace: true })
}

export const useLogout = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    retry: 1, // Retry only once on failure
    retryDelay: 1000, // Wait 1 second before retrying
    mutationFn: logout,
    onSuccess: () => {
      handleLogoutCleanup(queryClient, navigate)
    },
    onError: error => {
      handleLogoutCleanup(queryClient, navigate)
    },
  })
}

export const useValidateToken = () => {
  return useQuery({
    queryKey: [AUTH_QUERY_KEY, "validate"],
    queryFn: validateTokenApi,
    retry: false,
    enabled: !!getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  })
}

// Enhanced auth hook with token validation
export const useAuth = () => {
  const queryClient = useQueryClient()

  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError,
    refetch: refetchValidation,
  } = useValidateToken()

  const logoutMutation = useLogout()

  const user = validationData?.data?.user ?? null

  const permissions = user?.access ?? []
  const roles = user?.roles ?? []
  const isAuthenticated = !!validationData?.data?.valid
  const isLoading = isValidating

  // Handle invalid token
  useEffect(() => {
    if (!isValidating && validationError) removeToken()
  }, [isValidating, validationError])

  const login = (token, userData = null) => {
    setToken(token)
    if (userData) {
      queryClient.setQueryData([AUTH_QUERY_KEY, "validate"], {
        data: { valid: true, user: userData },
      })
    }
    refetchValidation()
  }

  const logout = async () => await logoutMutation.mutateAsync()

  const checkAuth = () => !!getToken() && isAuthenticated

  // Permission helper
  const hasPermission = perm => permissions.includes(perm)
  const hasAnyPermission = (permList = []) =>
    permList.some(p => permissions.includes(p))

  const hasRole = roleSlug => roles.some(r => r.slug === roleSlug)

  return {
    isAuthenticated,
    isLoading: isLoading && !!getToken(),
    user,
    roles,
    permissions,
    login,
    logout,
    checkAuth,
    hasPermission,
    hasAnyPermission,
    hasRole,
    validationError,
    refetchValidation,
  }
}

// Hook for protecting public routes (like login) - redirect if already logged in
export const usePublicRoute = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Redirect to home or intended page if already logged in
      const from = location.state?.from?.pathname || "/"
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  return { isLoading, isAuthenticated }
}

// Hook for protecting private routes - redirect to login if not authenticated
export const usePrivateRoute = () => {
  const { isAuthenticated, isLoading, refetchValidation } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const token = getToken()
    if (token && !isLoading) {
      refetchValidation()
    }
  }, [])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login page with return url
      navigate("/login", {
        state: { from: location },
        replace: true,
      })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  return { isLoading, isAuthenticated }
}

// Higher Order Component for public routes (login, register, etc.)
export const PublicRoute = ({ children }) => {
  const { isLoading } = usePublicRoute()

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return children
}
// Higher Order Component for private routes
export const PrivateRoute = ({ children, requiredPermissions = [] }) => {
  const { isLoading, isAuthenticated, hasAnyPermission } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  // Permission check (handled after authentication)
  if (
    !isLoading &&
    isAuthenticated &&
    requiredPermissions.length &&
    !hasAnyPermission(requiredPermissions)
  ) {
    return navigate("/401", { replace: true })
  }

  // Render protected children only if authenticated
  return isAuthenticated ? children : null
}

// Custom hook for components that need auth state
export const useAuthState = () => {
  const { isAuthenticated, isLoading, user } = useAuth()

  return {
    isAuthenticated,
    isLoading,
    user,
    isAdmin: user?.role === "admin", // Example role check
    isUserLoaded: !!user && !isLoading,
  }
}
